import { useFetchFn } from 'hooks/useFetchFn';
import type { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { searchAlgoliaFetchSimilarJobs } from 'modules/search/algolia/fetch/similarJobs/searchAlgoliaFetchSimilarJobs';
import { SearchHit } from 'modules/search/components/Hit/SearchHit';
import { SearchLoadingSectionMockResults } from 'modules/search/components/Loading/SearchLoadingSectionMockResults';
import { useSearchLocationOrEmpty } from 'modules/search/contexts/SearchContext';
import {
  type SearchSavedListingsStore,
  useSearchSavedListingsSyncAlgoliaHits,
} from 'modules/search/zustand-stores/searchSavedListingsStore';
import { algoliaTrackClick } from 'utils/algolia/algoliaTrackClick';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { ViewJobPageSidebarSuggestionsHolder } from './ViewJobPageSidebarSuggestions.styled';

type Props = {
  job: ApiJob;
  searchSavedListingsStore: SearchSavedListingsStore;
};

export function ViewJobPageSidebarSuggestions({
  job,
  searchSavedListingsStore,
}: Props) {
  const userSearchLocation = useSearchLocationOrEmpty();

  const [similarJobsResponse] = useFetchFn({
    fn: searchAlgoliaFetchSimilarJobs,
    args: [
      job.name,
      job.synonym,
      job.functions,
      userSearchLocation,
      job.org?.areasOfFocusKeys,
    ],
    // We don't want to change the results as the user navigates
    // But the search location might load after this functions is called, so make sure to clear it
    cacheKey: userSearchLocation
      ? 'with-search-location'
      : 'without-search-location',
  });

  useSearchSavedListingsSyncAlgoliaHits({
    hits: similarJobsResponse?.hits,
    searchSavedListingsStore,
  });

  return (
    <ViewJobPageSidebarSuggestionsHolder>
      {similarJobsResponse ? (
        similarJobsResponse.hits.map((hit, index) => (
          <SearchHit
            key={hit.objectID}
            hit={hit}
            searchSavedListingsStore={searchSavedListingsStore}
            hideOwnerBanner
            highlighted={job.id === hit.objectID}
            trackClick={() => {
              // Position in algolia analytics is 1-indexed
              const hitIndex =
                similarJobsResponse.page * similarJobsResponse.hitsPerPage +
                index +
                1;

              void algoliaTrackClick(
                similarJobsResponse.queryID,
                hit.objectID,
                hitIndex,
              );

              trackClicked('Search Result', {
                org_id: 'orgID' in hit ? hit.orgID : undefined,
                org_type: 'orgType' in hit ? hit.orgType : undefined,
                result_id: hit.objectID,
                result_index: index,
                result_type: hit.type,
              });
            }}
            actions={null}
            application={null}
            showLabel={false}
          />
        ))
      ) : (
        <SearchLoadingSectionMockResults items={3} />
      )}
    </ViewJobPageSidebarSuggestionsHolder>
  );
}
